/**
 * Cluvio parameter vocabulary.
 *
 * Timerange tokens follow Cluvio's documented parameter format:
 *   thisfull_<unit>   current full unit (today, this month, ...)
 *   this_<unit>       current unit, ending now (today so far, month-to-date, ...)
 *   previous_<unit>   previous full unit (yesterday, last month, ...)
 *   <n>_full_<unit>   last n full units (7_full_day = last 7 complete days)
 *   <n>_<unit>        last n units, ending now (rolling)
 *   <unix>~<unix>     absolute from/to
 * See https://cluvio.zendesk.com/hc/en-us/articles/360011556779-Parameters-in-URLs
 */

export interface TimerangePreset {
  label: string;
  value: string | null;
  group: string;
}

export const TIMERANGE_GROUP_ORDER = [
  'Current period',
  'To date',
  'Previous period',
  'Last N (full units)',
  'Last N (rolling, ends now)',
  'Other',
] as const;

export const TIMERANGE_PRESETS: TimerangePreset[] = [
  { label: 'Today', value: 'thisfull_day', group: 'Current period' },
  { label: 'This Week', value: 'thisfull_week', group: 'Current period' },
  { label: 'This Month', value: 'thisfull_month', group: 'Current period' },
  { label: 'This Quarter', value: 'thisfull_quarter', group: 'Current period' },
  { label: 'This Year', value: 'thisfull_year', group: 'Current period' },

  { label: 'Today so far', value: 'this_day', group: 'To date' },
  { label: 'Week to date', value: 'this_week', group: 'To date' },
  { label: 'Month to date', value: 'this_month', group: 'To date' },
  { label: 'Quarter to date', value: 'this_quarter', group: 'To date' },
  { label: 'Year to date', value: 'this_year', group: 'To date' },

  { label: 'Yesterday', value: 'previous_day', group: 'Previous period' },
  { label: 'Last Week', value: 'previous_week', group: 'Previous period' },
  { label: 'Last Month', value: 'previous_month', group: 'Previous period' },
  { label: 'Last Quarter', value: 'previous_quarter', group: 'Previous period' },
  { label: 'Last Year', value: 'previous_year', group: 'Previous period' },

  { label: 'Last 7 Days', value: '7_full_day', group: 'Last N (full units)' },
  { label: 'Last 14 Days', value: '14_full_day', group: 'Last N (full units)' },
  { label: 'Last 28 Days', value: '28_full_day', group: 'Last N (full units)' },
  { label: 'Last 30 Days', value: '30_full_day', group: 'Last N (full units)' },
  { label: 'Last 90 Days', value: '90_full_day', group: 'Last N (full units)' },
  { label: 'Last 3 Months', value: '3_full_month', group: 'Last N (full units)' },
  { label: 'Last 6 Months', value: '6_full_month', group: 'Last N (full units)' },
  { label: 'Last 12 Months', value: '12_full_month', group: 'Last N (full units)' },

  { label: 'Last 24 Hours', value: '24_hour', group: 'Last N (rolling, ends now)' },
  { label: 'Last 7 Days', value: '7_day', group: 'Last N (rolling, ends now)' },
  { label: 'Last 30 Days', value: '30_day', group: 'Last N (rolling, ends now)' },
  { label: 'Last 90 Days', value: '90_day', group: 'Last N (rolling, ends now)' },

  { label: 'All Time', value: null, group: 'Other' },
];

export const AGGREGATIONS = ['Minute', 'Hour', 'Day', 'Week', 'Month', 'Quarter', 'Year'] as const;

export const TENANT_TYPES = ['brand', 'internal', 'media-partner', 'self-serve', 'talent'] as const;

/** Flat { label, value } options for comboboxes. Duplicate labels across
 *  groups (e.g. "Last 7 Days" full vs. rolling) are disambiguated. */
export const TIMERANGE_OPTIONS: { label: string; value: string }[] = (() => {
  const counts = new Map<string, number>();
  for (const p of TIMERANGE_PRESETS) counts.set(p.label, (counts.get(p.label) ?? 0) + 1);
  return TIMERANGE_PRESETS.filter((p) => p.value !== null).map((p) => ({
    label:
      (counts.get(p.label) ?? 0) > 1
        ? `${p.label} (${p.group.startsWith('Last N (rolling') ? 'rolling' : 'full units'})`
        : p.label,
    value: p.value as string,
  }));
})();

export const AGGREGATION_OPTIONS: { label: string; value: string }[] = AGGREGATIONS.map((a) => ({
  label: a,
  value: a.toLowerCase(),
}));

/** Cluvio control_types whose values are multi-select (dropdown_multi*, input_multi). */
export function isMultiControl(controlType?: string | null): boolean {
  return !!controlType && controlType.endsWith('_multi');
}

/** Cluvio control_types that are boolean toggles (checkbox, checkbox_two_way). */
export function isCheckboxControl(controlType?: string | null): boolean {
  return !!controlType && controlType.startsWith('checkbox');
}

/** Cluvio control_type values whose defaults come from the timerange vocabulary. */
export const TIMERANGE_CONTROL_TYPES = new Set(['date_range', 'date_time_range']);
/** control_type values whose defaults come from the aggregation vocabulary. */
export const AGGREGATION_CONTROL_TYPES = new Set(['date_time_aggregation']);

/** Heuristic for contexts where only the filter key (variable_name) is known. */
export function presetsForFilterKey(key: string): { label: string; value: string }[] | undefined {
  const k = key.toLowerCase();
  if (k === 'timerange' || k.endsWith('timerange') || k.endsWith('daterange') || k.endsWith('date_range')) {
    return TIMERANGE_OPTIONS;
  }
  if (k === 'aggregation' || k.endsWith('_aggregation')) {
    return AGGREGATION_OPTIONS;
  }
  return undefined;
}
